import type { Tenant } from "@/payload-types";

/**
 * Closed enum of surfaces where profile consents can be collected.
 * Adding a new surface is a code change — by design.
 */
export type ConsentSurface = "signup" | "game:predictor" | "game:topx" | "game:fantasy";

export const ALL_CONSENT_SURFACES: ConsentSurface[] = [
    "signup",
    "game:predictor",
    "game:topx",
    "game:fantasy",
];

/**
 * Client-safe profile consent record.
 *
 * `body` is the localized rich-text payload (Lexical SerializedEditorState),
 * rendered with the existing <RichText> component.
 */
export interface ConsentDef {
    consentId: string;
    body: unknown;
    required: boolean;
    surfaces: ConsentSurface[];
    defaultChecked: boolean;
    enabled: boolean;
}

/**
 * Convert raw Payload-typed consents into client-safe ConsentDefs.
 * Filters out disabled entries and entries with no valid surfaces.
 */
export function extractConsents(tenant: Tenant | undefined): ConsentDef[] {
    const raw = tenant?.features?.consents ?? [];
    if (!Array.isArray(raw)) return [];

    return raw
        .filter((c) => c?.enabled !== false)
        .map((c) => ({
            consentId: c.consentId ?? "",
            body: c.body,
            required: c.required === true,
            surfaces: ((c.surfaces ?? []) as ConsentSurface[]).filter((s) =>
                ALL_CONSENT_SURFACES.includes(s)
            ),
            defaultChecked: c.defaultChecked === true,
            enabled: c.enabled !== false,
        }))
        .filter((c) => c.consentId && c.surfaces.length > 0);
}

/** Filter a list of consents to those applicable to a given surface. */
export function consentsForSurface(consents: ConsentDef[], surface: ConsentSurface): ConsentDef[] {
    return consents.filter((c) => c.surfaces.includes(surface));
}
